import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import DataEditor, { GridCellKind } from '@glideapps/glide-data-grid';
import '@glideapps/glide-data-grid/dist/index.css';
import { ChevronUpDownIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ALRSummary = ({
  alrDefects,
  dataEvents,
  thresholds,
  formatStation,
  dataSegments,
  projectConfig,
  selectedSegmentNumber,
  onSegmentSelect
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [sort, setSort] = useState({ column: 'fromStation', direction: 'asc' });
  const [activeFilter, setActiveFilter] = useState([]);

  // Parse URL params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const alrIdsParam = searchParams.get('selected_alrs');
    if (alrIdsParam) {
      setActiveFilter(alrIdsParam.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id)));
    } else {
      setActiveFilter([]);
    }
  }, [location.search]);

  // Valid segments for dropdown
  const validSegments = useMemo(() => {
    return (dataSegments || [])
      .map((seg, idx) => ({ ...seg, segmentNumber: idx + 1 }))
      .filter(seg => !seg.exception && seg.iri_avg_in_per_mi != null);
  }, [dataSegments]);

  const selectedSegment = useMemo(() => {
    if (!selectedSegmentNumber) return null;
    return validSegments.find(seg => seg.segmentNumber === selectedSegmentNumber);
  }, [selectedSegmentNumber, validSegments]);

  // IRI color scale from project config
  const COLOR_PALETTES = {
    2: ['#2EFF2E', '#D50000'],
    3: ['#2EFF2E', '#FFEB3B', '#D50000'],
    4: ['#2EFF2E', '#00A300', '#FFEB3B', '#D50000'],
    5: ['#2EFF2E', '#00A300', '#FFEB3B', '#FFA500', '#D50000'],
  };

  const IRI_COLOR_SCALE = useMemo(() => {
    const customRanges = projectConfig?.iri_color_scale;
    const DEFAULT_RANGES = [
      { label: "Excellent", max: 40 },
      { label: "Very Good", max: 60 },
      { label: "Satisfactory", max: 90 },
      { label: "Poor", max: 140 },
      { label: "Very Poor", max: 9999 }
    ];
    const isValid = Array.isArray(customRanges) && customRanges.length >= 2 && customRanges.length <= 5;
    const sourceRanges = isValid ? customRanges : DEFAULT_RANGES;
    const colors = COLOR_PALETTES[sourceRanges.length] || COLOR_PALETTES[5];
    return {
      ranges: sourceRanges.map((range, i) => ({ ...range, color: colors[i] })),
      defaultColor: '#A0A0A0'
    };
  }, [projectConfig]);

  const getSegmentColor = useCallback((segment) => {
    const iri = segment.iri_avg_in_per_mi || 0;
    if (iri <= 0) return IRI_COLOR_SCALE.defaultColor;
    for (const range of IRI_COLOR_SCALE.ranges) {
      if (iri <= range.max) return range.color;
    }
    return IRI_COLOR_SCALE.defaultColor;
  }, [IRI_COLOR_SCALE]);

  // Enrich ALRs with bump/dip counts
  const enrichedAlrDefects = useMemo(() => {
    return alrDefects.map(defect => {
      const from = defect.startDistance;
      const to = defect.stopDistance;
      const wp = defect.wheelpath;

      const associated = dataEvents.filter(e => {
        const type = (e.event_type || '').toUpperCase();
        if (type !== 'BUMP' && type !== 'DIP') return false;
        const inRange = (e.from_station_ft >= from && e.from_station_ft <= to) ||
                        (e.to_station_ft >= from && e.to_station_ft <= to) ||
                        (e.from_station_ft <= from && e.to_station_ft >= to);
        return inRange && (!wp || e.wheelpath === wp);
      });

      return {
        ...defect,
        bumpCount: associated.filter(e => (e.event_type || '').toUpperCase() === 'BUMP').length,
        dipCount: associated.filter(e => (e.event_type || '').toUpperCase() === 'DIP').length,
        bumps: associated.filter(e => (e.event_type || '').toUpperCase() === 'BUMP').sort((a, b) => a.from_station_ft - b.from_station_ft),
        dips: associated.filter(e => (e.event_type || '').toUpperCase() === 'DIP').sort((a, b) => a.from_station_ft - b.from_station_ft),
      };
    });
  }, [alrDefects, dataEvents]);

  // ALR count per segment for dropdown
  const segmentAlrCounts = useMemo(() => {
    const counts = new Map();
    validSegments.forEach(seg => {
      const count = enrichedAlrDefects.filter(alr =>
        (alr.startDistance >= seg.from_station_ft && alr.startDistance <= seg.to_station_ft) ||
        (alr.stopDistance >= seg.from_station_ft && alr.stopDistance <= seg.to_station_ft) ||
        (alr.peakDistance >= seg.from_station_ft && alr.peakDistance <= seg.to_station_ft) ||
        (alr.startDistance <= seg.from_station_ft && alr.stopDistance >= seg.to_station_ft)
      ).length;
      counts.set(seg.segmentNumber, count);
    });
    return counts;
  }, [validSegments, enrichedAlrDefects]);

  // Segment filter logic
  const handleClearFilter = useCallback(() => {
    setActiveFilter([]);
    onSegmentSelect(null);
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('selected_alrs');
    const newSearch = searchParams.toString();
    navigate(`${location.pathname}${newSearch ? '?' + newSearch : ''}`, { replace: true });
  }, [location.pathname, navigate, onSegmentSelect]);

  useEffect(() => {
    if (!selectedSegmentNumber) return;
    const segment = validSegments.find(s => s.segmentNumber === selectedSegmentNumber);
    if (!segment) return;

    const segStart = segment.from_station_ft;
    const segEnd = segment.to_station_ft;

    const ids = enrichedAlrDefects
      .filter(alr => {
        return (alr.startDistance >= segStart && alr.startDistance <= segEnd) ||
               (alr.stopDistance >= segStart && alr.stopDistance <= segEnd) ||
               (alr.peakDistance >= segStart && alr.peakDistance <= segEnd) ||
               (alr.startDistance <= segStart && alr.stopDistance >= segEnd);
      })
      .map(alr => alr.eventId);

    if (ids.length > 0) {
      const searchParams = new URLSearchParams(location.search);
      const currentIds = searchParams.get('selected_alrs');
      const newIds = ids.join(',');
      if (currentIds !== newIds) {
        setActiveFilter(ids);
        searchParams.set('selected_alrs', newIds);
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
      }
    } else {
      onSegmentSelect(null);
    }
  }, [selectedSegmentNumber, validSegments, enrichedAlrDefects, location.search, navigate, location.pathname, onSegmentSelect]);

  const handleSort = (column) => {
    setSort(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Filter & sort
  const filteredDefects = activeFilter.length > 0
    ? enrichedAlrDefects.filter(d => activeFilter.includes(d.eventId))
    : enrichedAlrDefects;

  const sortedDefects = [...filteredDefects].sort((a, b) => {
    const m = sort.direction === 'asc' ? 1 : -1;
    switch (sort.column) {
      case 'fromStation': return m * (a.startDistance - b.startDistance);
      case 'peak': return m * (a.magnitude - b.magnitude);
      case 'length': return m * ((a.length || 0) - (b.length || 0));
      case 'bumps': return m * (a.bumpCount - b.bumpCount);
      case 'dips': return m * (a.dipCount - b.dipCount);
      default: return 0;
    }
  });

  const gridColumns = useMemo(() => {
    const sortSuffix = (column) => sort.column === column ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : '';
    const dataColumns = [
      { id: 'fromStation', title: `Start Station${sortSuffix('fromStation')}`, width: 150 },
      { id: 'toStation', title: 'Stop Station', width: 150 },
      { id: 'peak', title: `Peak IRI${sortSuffix('peak')}`, width: 110 },
      { id: 'peakStation', title: 'Peak Station', width: 150 },
      { id: 'length', title: `Length${sortSuffix('length')}`, width: 105 },
      { id: 'wheelpath', title: 'Wheelpath', width: 105 },
      { id: 'bumps', title: `Bumps${sortSuffix('bumps')}`, width: 90 },
      { id: 'dips', title: `Dips${sortSuffix('dips')}`, width: 90 },
    ];
    const fillerColumns = Array.from({ length: 8 }, (_, index) => ({
      id: `blank-${index + 1}`,
      title: '',
      width: 110,
      grow: 1,
    }));

    return [...dataColumns, ...fillerColumns];
  }, [sort]);

  const gridRows = useMemo(() => sortedDefects.map(defect => ({
    fromStation: formatStation(defect.startDistance),
    toStation: formatStation(defect.stopDistance),
    peak: defect.magnitude?.toFixed(1) ?? '',
    peakStation: formatStation(defect.peakDistance),
    length: `${defect.length < 1 ? '<1' : defect.length.toFixed(1)} ft`,
    wheelpath: defect.wheelpath || '',
    bumps: String(defect.bumpCount || 0),
    dips: String(defect.dipCount || 0),
  })), [sortedDefects, formatStation]);

  const getCellContent = useCallback(([col, row]) => {
    const gridRow = gridRows[row];
    const column = gridColumns[col];
    const value = gridRow?.[column?.id] ?? '';

    return {
      kind: GridCellKind.Text,
      data: value,
      displayData: value,
      allowOverlay: false,
      readonly: true,
    };
  }, [gridColumns, gridRows]);

  const handleHeaderClick = useCallback((colIndex) => {
    const column = gridColumns[colIndex];
    if (!column || !['fromStation', 'peak', 'length', 'bumps', 'dips'].includes(column.id)) return;
    handleSort(column.id);
  }, [gridColumns]);

  // Stats
  const stats = useMemo(() => ({
    count: filteredDefects.length,
    total: enrichedAlrDefects.length,
    totalBumps: filteredDefects.reduce((s, d) => s + d.bumpCount, 0),
    totalDips: filteredDefects.reduce((s, d) => s + d.dipCount, 0),
    avgIRI: filteredDefects.length > 0 ? filteredDefects.reduce((s, d) => s + d.magnitude, 0) / filteredDefects.length : 0,
    maxIRI: filteredDefects.length > 0 ? Math.max(...filteredDefects.map(d => d.magnitude)) : 0,
  }), [filteredDefects, enrichedAlrDefects]);

  // Export
  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExport = (format) => {
    const data = filteredDefects.length > 0 ? filteredDefects : enrichedAlrDefects;
    if (format === 'csv') {
      const headers = ['Event ID', 'From Station', 'To Station', 'Peak Station', 'Magnitude (in/mi)', 'Wheelpath', 'Length (ft)', 'Bumps', 'Dips'];
      const rows = data.map(d => [d.eventId, formatStation(d.startDistance), formatStation(d.stopDistance), formatStation(d.peakDistance), d.magnitude.toFixed(2), d.wheelpath, d.length.toFixed(1), d.bumpCount, d.dipCount]);
      downloadFile([headers.join(','), ...rows.map(r => r.join(','))].join('\n'), 'alr-summary.csv', 'text/csv');
    } else if (format === 'txt') {
      const lines = data.map(d => `Event ${d.eventId}: ${formatStation(d.startDistance)} to ${formatStation(d.stopDistance)} | Peak: ${d.magnitude.toFixed(2)} in/mi | ${d.wheelpath} | ${d.bumpCount} bumps, ${d.dipCount} dips`);
      downloadFile(`ALR Summary Report\n${'='.repeat(50)}\n\n${lines.join('\n')}`, 'alr-summary.txt', 'text/plain');
    }
  };

  if (enrichedAlrDefects.length === 0) {
    return (
      <div className="bg-white h-[calc(100vh-95px)] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h4 className="text-lg font-medium text-gray-900 mb-2">No Areas of Localized Roughness</h4>
          <p className="text-gray-500">No ALRs detected above the {thresholds?.active || 'configured'} in/mi threshold</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white h-[calc(100vh-95px)] flex flex-col">
      {/* Header */}
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 h-20 shrink-0">
        <div className="flex items-center justify-between">
          {/* Left: Title + stats */}
          <div className="flex items-center gap-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Localized Roughness</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {activeFilter.length > 0 ? (
                  <>{stats.count} of {stats.total} ALRs</>
                ) : (
                  <>{stats.total} ALRs</>
                )}
              </p>
            </div>

            {/* Stats pills */}
            <div className="flex items-center gap-3 text-sm">
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-md">
                <span className="text-gray-500">Avg</span>
                <span className="font-semibold text-gray-800 font-mono tabular-nums">{stats.avgIRI.toFixed(1)}</span>
                <span className="text-gray-400">in/mi</span>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-md">
                <span className="text-gray-500">Max</span>
                <span className="font-semibold text-gray-800 font-mono tabular-nums">{stats.maxIRI.toFixed(1)}</span>
                <span className="text-gray-400">in/mi</span>
              </div>
            </div>
          </div>

          {/* Right: Filter + Export */}
          <div className="flex items-center gap-3">
            {/* Segment dropdown */}
            <Listbox value={selectedSegmentNumber} onChange={onSegmentSelect}>
              <div className="relative">
                <ListboxButton className="relative w-80 h-9 cursor-default rounded bg-white pl-3 pr-10 text-left text-sm border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500">
                  <span className="flex items-center gap-1.5">
                    {selectedSegment ? (
                      <>
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: getSegmentColor(selectedSegment) }} />
                        <span className="truncate">{formatStation(selectedSegment.from_station_ft)} — {formatStation(selectedSegment.to_station_ft)}</span>
                      </>
                    ) : (
                      <span className="text-gray-400">Filter by segment...</span>
                    )}
                  </span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                  </span>
                </ListboxButton>
                <ListboxOptions transition className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
                  {validSegments.map(segment => {
                    const alrCount = segmentAlrCounts.get(segment.segmentNumber) || 0;
                    return (
                      <ListboxOption key={segment.segmentNumber} value={segment.segmentNumber} disabled={alrCount === 0} className={`group relative select-none py-2 pl-3 pr-9 data-[selected]:bg-blue-100 ${alrCount === 0 ? 'opacity-40 cursor-not-allowed' : 'cursor-default hover:bg-blue-50'}`}>
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: getSegmentColor(segment) }} />
                            <span className="group-data-[selected]:font-semibold text-gray-900">{formatStation(segment.from_station_ft)} — {formatStation(segment.to_station_ft)}</span>
                          </span>
                          <span className="flex items-center gap-3">
                            <span className="text-gray-400">{segment.iri_avg_in_per_mi?.toFixed(1)}</span>
                            <span className={`text-xs font-medium w-14 text-right ${alrCount > 0 ? 'text-blue-600' : 'text-gray-400'}`}>{alrCount} ALR{alrCount !== 1 ? 's' : ''}</span>
                          </span>
                        </div>
                      </ListboxOption>
                    );
                  })}
                </ListboxOptions>
              </div>
            </Listbox>

            {activeFilter.length > 0 && (
              <button onClick={handleClearFilter} className="flex items-center gap-1 h-9 px-3 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors">
                <XMarkIcon className="h-4 w-4" />
                Clear
              </button>
            )}

            {/* Export */}
            <Menu as="div" className="relative">
              <MenuButton className="flex items-center gap-1 h-9 px-2.5 text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors">
                <ArrowDownTrayIcon className="h-4 w-4" />
                <ChevronDownIcon className="h-3.5 w-3.5" />
              </MenuButton>
              <MenuItems transition className="absolute right-0 z-20 mt-1 w-36 rounded bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
                <div className="py-1">
                  <MenuItem>{({ active }) => <button onClick={() => handleExport('csv')} className={`block w-full px-3 py-2 text-left text-sm ${active ? 'bg-gray-100' : ''}`}>Export CSV</button>}</MenuItem>
                  <MenuItem>{({ active }) => <button onClick={() => handleExport('txt')} className={`block w-full px-3 py-2 text-left text-sm ${active ? 'bg-gray-100' : ''}`}>Export TXT</button>}</MenuItem>
                </div>
              </MenuItems>
            </Menu>
          </div>
        </div>
      </div>

      {/* Spreadsheet grid */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {sortedDefects.length > 0 ? (
          <div className="h-full flex flex-col">
            <div className="flex-1 min-h-0">
              <DataEditor
                columns={gridColumns}
                rows={gridRows.length}
                getCellContent={getCellContent}
                getCellsForSelection={true}
                rowMarkers="number"
                rowHeight={36}
                headerHeight={38}
                smoothScrollX={true}
                smoothScrollY={true}
                rangeSelect="multi-rect"
                freezeColumns={1}
                onHeaderClicked={handleHeaderClick}
                theme={{
                  accentColor: '#2563eb',
                  accentLight: 'rgba(37, 99, 235, 0.12)',
                  fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
                  cellHorizontalPadding: 12,
                  headerFontStyle: '600 12px',
                  baseFontStyle: '13px',
                }}
              />
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center h-full">
            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-900 mb-1">No Results</h4>
              <p className="text-xs text-gray-500 mb-3">No ALRs match the current filter</p>
              <button onClick={handleClearFilter} className="text-xs text-blue-600 hover:text-blue-700 font-medium">Clear filter</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ALRSummary;
